import { Component, inject, OnInit } from '@angular/core';
import {
    ClaimAgeSummaryResponse,
    ClaimSummaryRequest,
} from '@core/generated/openapi';
import { ClaimService } from '@services/claim/claim.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotificationServiceUP } from '@services/notifications/notification-service-u-p.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ClaimAgeSearchService } from '@services/claim-search/claim-age-search.service';
import { ClaimStatusSearchService } from '@services/claim-search/claim-status-search.service';
import { ClaimSearchService } from '@services/claim-search/claim-search.service';
import { AuthService } from '@services/auth/auth.service';
import { isNil } from 'lodash-es';
import { STALLED_CLAIM_FILTER_IDS } from '@core/constants/stalled-claim-filter.constants';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';
import { skip } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'rcp-oup-aging-claims-summary',
    templateUrl: './aging-claims-summary.component.html',
    styleUrls: ['./aging-claims-summary.component.scss'],
    standalone: false,
})
export class AgingClaimsSummaryComponent implements OnInit {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );

    isLoading: boolean = false;
    allClaims: ClaimAgeSummaryResponse[];
    totalCount: number;
    totalBalance: number;
    locations: number[];

    constructor(
        private claimService: ClaimService,
        private notificationService: NotificationServiceUP,
        private router: Router,
        private route: ActivatedRoute,
        private claimAgeSearchService: ClaimAgeSearchService,
        private claimStatusSearchService: ClaimStatusSearchService,
        private claimSearchService: ClaimSearchService,
        private authService: AuthService,
    ) {}

    ngOnInit(): void {
        this.isLoading = true;

        this.authService.agingLocationFilter
            .pipe(untilDestroyed(this))
            .subscribe((locations) => {
                if (this.authService.isAgingUploadByLocationOff(locations)) {
                    this.isLoading = true;
                    this.locations = undefined;
                    this.loadData();
                } else if (!isNil(locations)) {
                    this.isLoading = true;
                    this.locations = locations;
                    this.loadData();
                }
            });

        this.onStalledFilterChange();
    }

    onStalledFilterChange() {
        this.claimStalledFilterService
            .onFilterStatusChange(STALLED_CLAIM_FILTER_IDS.AGING_CLAIMS_SUMMARY)
            .pipe(untilDestroyed(this), skip(1))
            .subscribe({
                next: () => this.loadData(),
            });
    }

    loadData() {
        this.isLoading = true;

        let request: ClaimSummaryRequest = {
            accountLocationIds: this.locations,
            nonStalledClaimsOnly:
                !this.claimStalledFilterService.getFilterStatusValue(
                    STALLED_CLAIM_FILTER_IDS.AGING_CLAIMS_SUMMARY,
                ),
        };

        this.claimService
            .getClaimsAgeSummary(request)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (result) => {
                    this.allClaims = result.claimAgeSummaries;
                    this.totalCount = result.totalCount;
                    this.totalBalance = result.totalBalance;
                },
                error: (error: any) => {
                    this.isLoading = false;
                    this.notificationService.showError(
                        'Error loading Aging Claim Summary.',
                        error.error,
                    );
                    console.error(error);
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                },
            });
    }

    calculateSummary(age: string): AgingSummaryItem {
        const totalBalance = this.totalBalance;
        if (!this.allClaims) {
            return {
                agingRange: age,
                count: 0,
                balance: 0,
                percentage: 0,
            };
        }
        const claim = this.allClaims.find((claim) => claim.agingRange === age);

        if (claim) {
            const percentage = ((claim.balance || 0) / totalBalance) * 100;
            return {
                agingRange: claim.agingRange,
                count: claim.count || 0,
                balance: claim.balance || 0,
                percentage: isNaN(percentage) ? 0 : percentage,
            };
        } else {
            return {
                agingRange: age,
                count: 0,
                balance: 0,
                percentage: 0,
            };
        }
    }

    goToClaimAgeFilter(routeOption: number) {
        this.claimAgeSearchService.clearPayorSnoozeFilters();
        this.claimSearchService.setClaimsCount(0);
        this.setFilterForClaims(STALLED_CLAIM_FILTER_IDS.BY_AGE);
        this.router.navigate(['../claims/search/age/' + routeOption], {
            relativeTo: this.route,
        });
    }

    goToClaimStatusFilter() {
        this.claimStatusSearchService.clearPayorSnoozeFilters();
        this.claimSearchService.setClaimsCount(0);
        this.setFilterForClaims(STALLED_CLAIM_FILTER_IDS.BY_STATUS);
        this.router.navigate(['../claims/search/status/0'], {
            relativeTo: this.route,
        });
    }

    setFilterForClaims(id: string) {
        const summaryFilterValue =
            this.claimStalledFilterService.getFilterStatusValue(
                STALLED_CLAIM_FILTER_IDS.AGING_CLAIMS_SUMMARY,
            );
        this.claimStalledFilterService.updateFilterStatus(
            id,
            summaryFilterValue,
        );
    }

    protected readonly STALLED_CLAIM_FILTER_IDS = STALLED_CLAIM_FILTER_IDS;
}

interface AgingSummaryItem {
    agingRange: string;
    count: number;
    balance: number;
    percentage: number;
}
